import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  NgZone,
  OnDestroy,
  output,
  signal,
  untracked,
  ViewChild,
} from "@angular/core";

/** One candidate string plus its natural (unclipped) rendered width, in CSS px. */
export interface FitCandidate {
  text: string;
  width: number;
}

/**
 * The first candidate (in order - most information-preserving first) whose natural width fits
 * within `availableWidth`; the last one if none do. Callers still wrap whichever text comes back
 * in a safety-net scrollable box (see the template), so even that last-resort case never
 * overlaps a sibling or breaks the surrounding layout - it just becomes scrollable.
 */
export function pickFittingText(availableWidth: number, candidates: FitCandidate[]): string {
  const fitting = candidates.find((candidate) => candidate.width <= availableWidth);
  return (fitting ?? candidates[candidates.length - 1]).text;
}

/**
 * Renders a currency amount that may be too wide for its container (gloss-client #5117), picking
 * the best-fitting of up to four candidates by *measuring* rather than guessing from digit count:
 *
 * 1. `fullText` - full precision, full symbol (e.g. "A$246,913,579,299").
 * 2. `fullTextNarrow` - full precision, bare symbol (e.g. "$246,913,579,299") - falls back to
 *    `fullText` if not given.
 * 3. `compactText` - the ticket's abbreviated form, full symbol (e.g. "A$247B").
 * 4. `compactTextNarrow` - abbreviated, bare symbol (e.g. "$247B") - falls back to `compactText`.
 *
 * Each candidate's own *natural* width is read from a hidden, `position: absolute` /
 * `visibility: hidden` probe span (same `textClass`, so it's measured under the exact font/size
 * the visible text would use) - these never affect layout, they just report how wide each option
 * would need to be given infinite room.
 *
 * *Available* width is a different measurement, taken from a separate `availabilitySizer` span
 * that (unlike the probes) stays in normal flow and always renders `fullText` - the widest
 * candidate - so it gets genuinely shrunk/stretched/clamped by the real surrounding layout exactly
 * like real content would be. Reading its clamped `clientWidth` (not the probes' unclamped
 * `scrollWidth`) is what lets both directions work: measuring the *currently selected* text's own
 * box would only ever reveal that space ran out (that box shrinks to match whatever's already
 * selected, so it can never notice more room reopening), whereas a box that always asks for the
 * widest candidate's width correctly reports the true ceiling either way a resize goes. A
 * `ResizeObserver` on this sizer re-runs the selection whenever that ceiling changes.
 *
 * Together these pick the first candidate that fits - so a genuinely huge number still shows in
 * full precision whenever there's room (a wide screen, a roomy card), and only degrades to a
 * shorter form once the layout actually runs out of space, instead of a fixed digit-count
 * threshold deciding that up front regardless of how much room there really is.
 *
 * The initial render optimistically shows `fullText` (before the first measurement has run) so
 * there's never a flash of the compact form while the page is still settling.
 *
 * The host itself is always `tw-min-w-0 tw-max-w-full`: an ancestor flex item's automatic minimum
 * size is otherwise its unclipped content size (the classic flexbox min-width/height:auto gotcha),
 * which would stop a tight parent from ever handing this component less than its content's full
 * natural size in the first place, and the sizer would just silently be handed that same too-big
 * size instead of the row's real constraint. Deliberately *not* included is `shrink-0`: on
 * whichever axis is its flex parent's main axis, that would stop the host (and the in-flow sizer
 * inside it) from ever being squeezed down in the first place, so it'd overflow its row instead of
 * shrinking - reproducing the exact layout break (gloss-client #5117) this component exists to
 * prevent. Callers whose flex parent's main axis is the *other* (non-measured) one - e.g. a
 * `flex-col` hero card, where width is only the cross axis - can still add `shrink-0` themselves
 * at the call site to stop *that* axis from clipping, same as any other flex child; it doesn't
 * fight the width measurement there. The visible box also explicitly pins `overflow-y-hidden` next
 * to its `overflow-x-auto` - per the CSS overflow spec, one non-`visible` axis otherwise forces the
 * other to compute as `auto` too, turning a small vertical squeeze into an actual (and, worse,
 * accidentally mouse-scrollable) clipped axis instead of a harmless one.
 */
@Component({
  selector: "app-large-amount",
  standalone: true,
  templateUrl: "./large-amount.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "tw-relative tw-block tw-min-w-0 tw-max-w-full",
    "[class.tw-text-right]": "alignEnd()",
  },
})
export class LargeAmountComponent implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
  private readonly resizeObserver: ResizeObserver;

  /** Fully-expanded formatted text, e.g. "A$246,913,579,299" or "14,249,706,938 AUD". */
  readonly fullText = input.required<string>();
  /** Ticket-suggested abbreviated text, e.g. "A$247B" or "14.25B AUD". */
  readonly compactText = input.required<string>();
  /** Full precision, bare-symbol alternative (e.g. "$4,504" vs "A$4,504"); falls back to
   *  `fullText` if not given. */
  readonly fullTextNarrow = input<string | undefined>(undefined);
  /** Abbreviated, bare-symbol alternative; falls back to `compactText` if not given. */
  readonly compactTextNarrow = input<string | undefined>(undefined);
  /** Classes applied to the text itself, so callers can match surrounding font-size/weight. */
  readonly textClass = input("");
  /**
   * Right-align the visible text within the host instead of the default left-align. The *host* is
   * sized for the widest candidate that currently fits (see the availability-sizer note below) -
   * the visible box itself always shrinks to whatever candidate is actually selected, so whenever
   * a *shorter* candidate ends up selected while the host is still wide enough for something
   * longer, there's leftover space on one side of it inside the host. Left-aligned (the default) is
   * right for callers with adjacent text *before* the number (e.g. a "+"/"-" prefix); set this for
   * callers with adjacent text *after* it (e.g. a trailing currency code like "... AUD)"), so the
   * number stays flush against that trailing text instead of leaving a gap in front of it.
   */
  readonly alignEnd = input(false);
  /**
   * Static text rendered immediately before/after the number, *inside* the same shrink-to-fit box
   * as the number itself (see `alignEnd` above) - included in every width measurement (the probes
   * and the availability sizer render `prefix + candidate + suffix`, not just the bare candidate),
   * so the pair is always sized and selected as one unit. Placing adjacent text like this instead
   * of as sibling text next to `<app-large-amount>` avoids a gap opening up between them: sibling
   * text has no way to know how much of the host's (ceiling-sized) width the current candidate
   * actually used, so any leftover space is stranded between the two. Any leftover space here
   * instead lands outside the whole `prefix + text + suffix` group, e.g. before a leading "(" -
   * far less noticeable than a gap splitting a value that's meant to read as one string, like
   * "(+ 580M AUD)".
   */
  readonly prefix = input("");
  /** See `prefix`. */
  readonly suffix = input("");
  /**
   * Opt-in, for a group of amounts that should all switch form together (e.g. the Future tab's
   * path tabs, #6169): shows only the compact candidates, while still measuring - and reporting
   * through `fullFits` - whether a full one would fit here. Off by default.
   */
  readonly forceCompact = input(false);
  /**
   * Whether a full candidate (`fullText` or `fullTextNarrow`) fits the available width, after each
   * measurement that changes it - regardless of `forceCompact`, so a group can force compact while
   * any member's doesn't fit and release it once every member's does.
   */
  readonly fullFits = output<boolean>();
  private lastFullFits?: boolean;

  @ViewChild("fullProbe") private fullProbeRef?: ElementRef<HTMLSpanElement>;
  @ViewChild("fullNarrowProbe") private fullNarrowProbeRef?: ElementRef<HTMLSpanElement>;
  @ViewChild("compactProbe") private compactProbeRef?: ElementRef<HTMLSpanElement>;
  @ViewChild("compactNarrowProbe") private compactNarrowProbeRef?: ElementRef<HTMLSpanElement>;
  @ViewChild("availabilitySizer") private availabilitySizerRef?: ElementRef<HTMLSpanElement>;

  private resizeObserversWired = false;

  protected readonly effectiveFullTextNarrow = computed(
    () => this.fullTextNarrow() ?? this.fullText(),
  );
  protected readonly effectiveCompactTextNarrow = computed(
    () => this.compactTextNarrow() ?? this.compactText(),
  );

  protected readonly selectedText = signal("");

  constructor() {
    // ResizeObserver callbacks aren't zone-patched in this app, so re-enter the zone to make
    // sure the signal write below actually triggers a re-render (see the same pattern in
    // transaction-info-panel.component.ts).
    this.resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.measureAndSelect()));

    effect(
      () => {
        // Show the fullest text optimistically as soon as the candidate *content* changes, then
        // let the measurement below correct it on the next render once the probes have the
        // current text and real layout has happened - avoids a flash of the compact form on
        // every content change. Deliberately depends only on the text-content inputs, not on
        // textClass/prefix/suffix (see the second effect below): those change how the same
        // content is measured/decorated, not what it says, so resetting to fullText for them
        // would flash back to full precision even when a narrower candidate was already
        // correctly selected and the available width never changed.
        // (The compact one when forced: there's no full form to flash then.)
        this.selectedText.set(untracked(this.forceCompact) ? this.compactText() : this.fullText());
        this.effectiveFullTextNarrow();
        this.compactText();
        this.effectiveCompactTextNarrow();
        this.scheduleRemeasure();
      },
      { allowSignalWrites: true },
    );

    // textClass/prefix/suffix affect how wide each candidate measures (or add fixed text around
    // it) without changing which candidate is the "fullest" one - so these just trigger a
    // remeasurement, without the optimistic reset-to-fullText above.
    effect(() => {
      this.textClass();
      this.prefix();
      this.suffix();
      this.forceCompact();
      this.scheduleRemeasure();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  private scheduleRemeasure(): void {
    afterNextRender(
      () => {
        // The probe/sizer refs only exist once the view has rendered at least once, so wiring
        // the observer up is deferred to here rather than the constructor; they're the same DOM
        // nodes on every later run, so only do this once.
        if (
          !this.resizeObserversWired &&
          this.availabilitySizerRef &&
          this.fullProbeRef &&
          this.fullNarrowProbeRef &&
          this.compactProbeRef &&
          this.compactNarrowProbeRef
        ) {
          // Observing the four (position:absolute, intrinsically-sized) probes in addition to
          // the availability sizer means a pure font-size change - OS/browser text-only zoom, or
          // an accessibility large-text setting - triggers a remeasurement too: that kind of zoom
          // changes each probe's own natural width without necessarily changing the sizer's
          // container-driven box size, so relying on the sizer alone would leave a
          // previously-fitting candidate stuck overflowing after such a zoom.
          this.resizeObserver.observe(this.availabilitySizerRef.nativeElement);
          this.resizeObserver.observe(this.fullProbeRef.nativeElement);
          this.resizeObserver.observe(this.fullNarrowProbeRef.nativeElement);
          this.resizeObserver.observe(this.compactProbeRef.nativeElement);
          this.resizeObserver.observe(this.compactNarrowProbeRef.nativeElement);
          this.resizeObserversWired = true;
        }
        this.measureAndSelect();
      },
      { injector: this.injector },
    );
  }

  private measureAndSelect(): void {
    if (
      !this.fullProbeRef ||
      !this.fullNarrowProbeRef ||
      !this.compactProbeRef ||
      !this.compactNarrowProbeRef ||
      !this.availabilitySizerRef
    ) {
      return;
    }

    const availableWidth = this.availabilitySizerRef.nativeElement.clientWidth;
    const candidates: FitCandidate[] = [
      { text: this.fullText(), width: this.fullProbeRef.nativeElement.scrollWidth },
      {
        text: this.effectiveFullTextNarrow(),
        width: this.fullNarrowProbeRef.nativeElement.scrollWidth,
      },
      { text: this.compactText(), width: this.compactProbeRef.nativeElement.scrollWidth },
      {
        text: this.effectiveCompactTextNarrow(),
        width: this.compactNarrowProbeRef.nativeElement.scrollWidth,
      },
    ];
    const fullFits = candidates.slice(0, 2).some((candidate) => candidate.width <= availableWidth);
    if (fullFits !== this.lastFullFits) {
      this.lastFullFits = fullFits;
      this.fullFits.emit(fullFits);
    }
    this.selectedText.set(
      pickFittingText(availableWidth, this.forceCompact() ? candidates.slice(2) : candidates),
    );
  }
}
